import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import {
  AxiomLogo,
  Gutter,
  isSafari,
  Loader,
  Paragraph,
  Grid,
  GridColumn,
  GridRow,
  Flashy,
  PageContext,
  CookieUtil,
} from '@axiom/ui';

import { EnvUtil } from '../utils/env-util';
import { DefaultLayout } from '../layouts/DefaultLayout/DefaultLayout';
import sideImageUrl from '../public/svg/fingerprint.svg?url';
import { LoginFlowUtil } from '../utils/login-flow-util';

type SSOState = {
  loaded: boolean;
  error: boolean;
};

export const SSOPage = () => {
  const { setTitle } = useContext(PageContext);
  const navigate = useNavigate();
  const location = useLocation();

  const query = new URLSearchParams(location.search);
  const [ssoState, setSsoState] = useState<SSOState>({
    loaded: false,
    error: false,
  });

  useEffect(() => {
    setTitle('Axiom Law - Signing On');
  }, []);

  useEffect(() => {
    const userParam = query.get('user');
    if (userParam) {
      CookieUtil.setUserCookie(userParam, {
        domain: EnvUtil.cookieDomain,
        isSafari: isSafari(),
      });
    }

    void LoginFlowUtil.getRedirectUrl({ location })
      .then(({ url, replace }) => {
        if (url) {
          if (url.startsWith('http') || url.startsWith('//')) {
            // Safari complains if a API is in flight and the page unloads, this will help mitigate.
            setTimeout(() => {
              window.location.href = url;
            }, 500);
          } else {
            void navigate(url, { replace });
          }
          setSsoState({
            loaded: true,
            error: false,
          });
        } else {
          setSsoState({
            loaded: true,
            error: true,
          });
        }
      })
      .catch(() => {
        /**
         * If we get an error from fetching the logged in user, it probably means they have an
         * invalid token, because before we even get to this point, we've sanity checked for a
         * cookie and that it hasn't expired already
         */
        CookieUtil.clearUser(EnvUtil.cookieDomain);
        window.location.href = '/';
      });
  }, []);

  if (!ssoState.loaded) {
    return <Loader name="SSO_LOADER" type="logo" expand="fullscreen" />;
  }
  return (
    <DefaultLayout name="SSO_LAYOUT" sideImageUrl={sideImageUrl}>
      <Grid centered>
        <GridRow>
          <GridColumn
            largeScreenWidth={9}
            tabletWidth={9}
            mobileWidth={12}
            textAlign="center"
          >
            <Gutter bottom="24px" className="text-align-center">
              <AxiomLogo width="230px" />
            </Gutter>
            <Gutter bottom="24px">
              {ssoState.error ? (
                <Flashy name="ERROR_MESSAGE" color="textAlert">
                  You are not authorized to go anywhere!
                </Flashy>
              ) : (
                <Paragraph name="REDIRECT_MESSAGE">
                  You are being redirected...
                </Paragraph>
              )}
            </Gutter>
          </GridColumn>
        </GridRow>
      </Grid>
    </DefaultLayout>
  );
};
